import { useState } from "react";
import { toast } from "sonner";
import { FileText, Play, SkipForward, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, useApi, useLoad, errMsg } from "@/lib/api";
import { Empty } from "@/components/admin/kit";

const ITEM = (i) => ({ patient_id: i.patient_id, kind: i.kind, slot: i.slot, medicine_id: i.medicine_id, appointment_id: i.appointment_id });

export function ScriptBox({ s, testid, voice }) {
  if (!s) return null;
  return (
    <div className="rounded-xl bg-[var(--lav)] p-3 text-sm" data-testid={testid}>
      <p className="font-te leading-relaxed">{s.text}</p>
      <p className="mt-1 text-xs text-muted-foreground">Voice {voice || s.voice} · about {s.est_seconds}s</p>
      {s.audio_url && <audio controls autoPlay src={`${API.replace(/\/api$/, "")}${s.audio_url}`} className="mt-2 h-9 w-full" data-testid={`${testid}-audio`} />}
    </div>
  );
}

export function CallActions({ r, reload, tid }) {
  const api = useApi();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const live = r.items.filter((i) => !i.skipped);
  const skipped = !live.length;
  const past = new Date(`${r.date}T${r.time}:00+05:30`) < new Date();
  if (past) return <p className="text-xs text-muted-foreground md:col-span-3">Time has passed</p>;
  const go = async (audio) => {
    setBusy(audio ? "play" : "show");
    try { const x = await api.post("/scheduler/preview", { date: r.date, time: r.time, phone: r.phone, items: live.map(ITEM), audio }); setS(x.data); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const skip = async (on) => {
    if (on && !window.confirm(`Skip the ${r.time} call on ${r.date} to ${r.receiver}? Only this one call is skipped.`)) return;
    setBusy("skip");
    try { await api.post("/scheduler/skip", { date: r.date, items: (on ? live : r.items).map(ITEM), skip: on }); toast.success(on ? "This call will be skipped" : "Call back in the plan"); reload?.(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  return (
    <div className="grid gap-2 md:col-span-3">
      <div className="flex flex-wrap gap-2">
        {skipped ? <span className="self-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-bold" data-testid={`${tid}-skipped`}>Skipped</span> : <>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => go(false)} data-testid={`${tid}-show`}><FileText size={14} /> Show script</Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => go(true)} data-testid={`${tid}-play`}><Play size={14} /> {busy === "play" ? "Making audio…" : "Play (phone quality)"}</Button>
        </>}
        {skipped ? <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => skip(false)} data-testid={`${tid}-unskip`}><Undo2 size={14} /> Undo skip</Button>
          : <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => skip(true)} data-testid={`${tid}-skip`}><SkipForward size={14} /> Skip this call</Button>}
      </div>
      <ScriptBox s={s} voice={r.voice} testid={`${tid}-script`} />
    </div>
  );
}

export function UpcomingCalls({ pid }) {
  const [rows, reload] = useLoad("/call-schedule?days=7");
  const mine = (rows || []).filter((r) => r.items.some((i) => i.patient_id === pid));
  return (
    <div className="rounded-3xl bg-white p-5 shadow-sm" data-testid="patient-upcoming-calls">
      <p className="mb-2 font-bold">Upcoming calls (7 days)</p>
      {!rows ? <Empty>Loading…</Empty> : !mine.length ? <Empty>No calls planned.</Empty> : mine.map((r, ri) => (
        <div key={`${r.date}${r.time}-${ri}`} className="grid gap-1 border-t py-2 text-sm">
          <p><b>{r.date} {r.time}</b> · {r.phone} · {r.items.map((i) => `${i.label}${i.patient_id !== pid ? ` (${i.patient})` : ""}${i.skipped ? " – skipped" : ""}`).join(", ")}</p>
          <CallActions r={r} reload={reload} tid={`pcall-${r.date}-${r.time}`} />
        </div>
      ))}
    </div>
  );
}
